import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import {
  X, ChevronRight, ChevronLeft, Copy, Check, ExternalLink,
  Loader2, CheckCircle, AlertCircle, RefreshCw, Eye, EyeOff, Key
} from "lucide-react";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import ChannelIcon, { FacebookIcon as Facebook } from "../ChannelIcon";
import QueueSelect from "../QueueSelect";

// ─── Helpers ─────────────────────────────────────────────────────────────────

const generateVerifyToken = () =>
  Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);

const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button
      type="button"
      onClick={handleCopy}
      className="flex-shrink-0 p-1.5 rounded-md text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-950/30 transition-colors"
      title="Copiar"
    >
      {copied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  );
};

const CodeBlock = ({ value }) => (
  <div className="flex items-center gap-2 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2">
    <code className="text-xs text-slate-700 dark:text-slate-300 flex-1 break-all font-mono">{value}</code>
    <CopyButton text={value} />
  </div>
);

const Field = ({
  label,
  name,
  value,
  onChange,
  placeholder,
  type = "text",
  required,
  helpText,
  canCopy = false,
  canToggleVisibility = false
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const effectiveType = canToggleVisibility ? (showPassword ? "text" : "password") : type;

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        {value && canCopy && (
          <CopyButton text={value} />
        )}
      </div>
      <div className="relative flex items-center">
        <input
          type={effectiveType}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className={`w-full px-3 py-2.5 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all font-mono ${
            canToggleVisibility ? "pr-10" : ""
          }`}
        />
        {canToggleVisibility && (
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-2.5 p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
            title={showPassword ? "Ocultar" : "Mostrar"}
          >
            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        )}
      </div>
      {helpText && <p className="mt-1 text-xs text-slate-400">{helpText}</p>}
    </div>
  );
};

// ─── Step components ──────────────────────────────────────────────────────────

const channelStyles = {
  whatsapp_cloud: {
    activeBorder: "border-[#075E54] ring-2 ring-[#075E54]/20",
    activeBg: "bg-emerald-50/40 dark:bg-emerald-950/10",
    iconBg: "bg-emerald-100/75 dark:bg-emerald-950/40",
    badgeColor: "bg-[#075E54]"
  },
  whatsapp_coexistence: {
    activeBorder: "border-[#a855f7] ring-2 ring-[#a855f7]/20",
    activeBg: "bg-purple-50/40 dark:bg-purple-950/10",
    iconBg: "bg-purple-100/75 dark:bg-purple-950/40",
    badgeColor: "bg-[#a855f7]"
  },
  facebook: {
    activeBorder: "border-[#1877F2] ring-2 ring-[#1877F2]/20",
    activeBg: "bg-blue-50/40 dark:bg-blue-950/10",
    iconBg: "bg-blue-100/75 dark:bg-blue-950/40",
    badgeColor: "bg-[#1877F2]"
  },
  instagram: {
    activeBorder: "border-[#C13584] ring-2 ring-[#C13584]/20",
    activeBg: "bg-pink-50/40 dark:bg-pink-950/10",
    iconBg: "bg-pink-100/75 dark:bg-pink-950/40",
    badgeColor: "bg-[#C13584]"
  }
};

const StepChannelSelect = ({ channel, onChange }) => (
  <div className="space-y-4">
    <p className="text-sm text-slate-600 dark:text-slate-400">
      Selecciona el tipo de canal que deseas conectar:
    </p>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
      {[
        { key: "whatsapp_cloud", title: "WhatsApp Cloud", desc: "API Oficial de Meta para WhatsApp Business" },
        { key: "whatsapp_coexistence", title: "WhatsApp Coexistencia", desc: "API de WhatsApp Cloud con App manual" },
        { key: "facebook", title: "Facebook Messenger", desc: "Mensajes de tu Página de Facebook" },
        { key: "instagram", title: "Instagram DM", desc: "Mensajes directos de Instagram Business" }
      ].map(({ key, title, desc }) => {
        const isSelected = channel === key;
        const style = channelStyles[key];

        return (
          <button
            key={key}
            type="button"
            onClick={() => onChange(key)}
            className={`relative flex flex-col items-center justify-between p-5 rounded-2xl border-2 h-full min-h-[190px] transition-all duration-300 shadow-sm hover:shadow-md ${
              isSelected
                ? `${style.activeBorder} ${style.activeBg}`
                : "border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 bg-white dark:bg-slate-900"
            }`}
          >
            {/* Selector superior derecho */}
            {isSelected && (
              <div className={`absolute top-2.5 right-2.5 w-5 h-5 rounded-full flex items-center justify-center text-white ${style.badgeColor} shadow-sm animate-in zoom-in duration-250`}>
                <CheckCircle className="w-3.5 h-3.5" strokeWidth={3} />
              </div>
            )}

            {/* Burbuja del Icono */}
            <div className={`p-4 rounded-2xl ${isSelected ? style.iconBg : "bg-slate-50 dark:bg-slate-800"} transition-all duration-300 mb-2`}>
              <ChannelIcon channel={key} size={28} />
            </div>

            {/* Detalles del Texto */}
            <div className="text-center flex-1 flex flex-col justify-center">
              <p className="text-xs font-bold text-slate-800 dark:text-slate-200 tracking-wide uppercase mb-1">{title}</p>
              <p className="text-[11px] leading-relaxed text-slate-400 dark:text-slate-500 font-medium px-1">{desc}</p>
            </div>
          </button>
        );
      })}
    </div>
  </div>
);

const StepCredentials = ({ channel, form, onChange, isEdit, webhookUrl, onCopyAll }) => {
  const isFacebook = channel === "facebook";
  const isWhatsAppCloud = channel === "whatsapp_cloud" || channel === "whatsapp_coexistence";
  const docsUrl = isWhatsAppCloud
    ? "https://developers.facebook.com/docs/whatsapp/cloud-api/get-started"
    : isFacebook
    ? "https://developers.facebook.com/docs/messenger-platform/getting-started"
    : "https://developers.facebook.com/docs/instagram-platform/instagram-api-with-instagram-login";

  return (
    <div className="space-y-5">
      {/* Panel para migración / respaldo cuando se edita o hay datos */}
      {isEdit && (
        <div className="bg-indigo-50/60 dark:bg-indigo-950/20 border border-indigo-200 dark:border-indigo-800/60 rounded-xl p-3.5 space-y-2">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <Key className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
              <h3 className="text-xs font-bold text-indigo-900 dark:text-indigo-200 uppercase tracking-wider">
                Credenciales de conexión para migración
              </h3>
            </div>
            {onCopyAll && (
              <button
                type="button"
                onClick={onCopyAll}
                className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors shadow-sm"
                title="Copiar todas las credenciales en formato JSON"
              >
                <Copy className="w-3.5 h-3.5" />
                Copiar todo (JSON)
              </button>
            )}
          </div>
          <p className="text-xs text-indigo-700/80 dark:text-indigo-300/80">
            Puedes ver y copiar libremente el Token, la Clave Secreta y todos los identificadores de esta conexión para mudarla a otra versión o plataforma.
          </p>
        </div>
      )}

      {!isEdit && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
          <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 mb-1">Antes de continuar necesitas:</p>
          <ol className="text-xs text-amber-700 dark:text-amber-300 space-y-1 list-decimal list-inside">
            <li>Una <strong>Meta Developer App</strong> en <a href="https://developers.facebook.com" target="_blank" rel="noreferrer" className="underline">developers.facebook.com</a></li>
            {isWhatsAppCloud ? (
              <>
                <li>Una cuenta de <strong>WhatsApp Business</strong> configurada</li>
                <li>El <strong>ID de Teléfono (Phone Number ID)</strong> de tu número</li>
                <li>El <strong>ID de Cuenta de WhatsApp Business (WABA ID)</strong></li>
                <li>Un <strong>Token de Acceso Permanente</strong> (System User Token)</li>
              </>
            ) : isFacebook ? (
              <>
                <li>Una <strong>Página de Facebook</strong> con permisos de administrador</li>
                <li>El <strong>Page Access Token</strong> de esa página (permanente)</li>
              </>
            ) : (
              <>
                <li>Una <strong>cuenta de Instagram Business</strong> vinculada a una Página de Facebook</li>
                <li>Habilitado el producto <strong>Instagram Messaging</strong> en tu app</li>
                <li>Un <strong>Page Access Token</strong> con permisos de Instagram</li>
              </>
            )}
          </ol>
          <a
            href={docsUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 mt-2 text-xs text-amber-700 dark:text-amber-400 font-medium underline"
          >
            Ver documentación <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      )}

      <div className="space-y-3">
        <Field
          label="Nombre de la conexión"
          name="name"
          value={form.name}
          onChange={onChange}
          placeholder={isWhatsAppCloud ? "Mi WhatsApp Cloud" : isFacebook ? "Mi Página de Facebook" : "Mi Instagram Business"}
          required
        />
        <Field
          label="App ID de Meta"
          name="facebookAppId"
          value={form.facebookAppId}
          onChange={onChange}
          placeholder="123456789012345"
          canCopy={true}
          required
          helpText="Encuéntralo en: Meta Developers → Tu App → Configuración → Básica"
        />
        <Field
          label="App Secret de Meta (Clave Secreta)"
          name="facebookAppSecret"
          value={form.facebookAppSecret}
          onChange={onChange}
          placeholder={isEdit ? "Clave secreta guardada" : "abc123def456..."}
          type="password"
          canCopy={true}
          canToggleVisibility={true}
          required={!isEdit}
          helpText="Meta Developers → Configuración → Básica → Clave secreta de la app"
        />
        {isWhatsAppCloud && (
          <Field
            label="ID de Cuenta de WhatsApp Business (WABA ID)"
            name="wabaId"
            value={form.wabaId}
            onChange={onChange}
            placeholder="1234567890123"
            canCopy={true}
            required
            helpText="Encuéntralo en: Configuración del negocio → Cuentas de WhatsApp"
          />
        )}
        <Field
          label={
            isWhatsAppCloud 
              ? "ID del Número de Teléfono (Phone Number ID)" 
              : isFacebook 
              ? "ID de Página de Facebook" 
              : "ID de la Cuenta de Instagram Business"
          }
          name="facebookPageId"
          value={form.facebookPageId}
          onChange={onChange}
          placeholder="123456789"
          canCopy={true}
          required
          helpText={
            isWhatsAppCloud 
              ? "Encuéntralo en: WhatsApp → Configuración de la API → ID del número de teléfono" 
              : isFacebook 
              ? "Encuéntralo en: Página de Facebook → Acerca de → ID de Página" 
              : "Encuéntralo en: Meta Developers → Instagram → Configuración de la API → Identificador de cuenta de Instagram"
          }
        />
        <Field
          label={isWhatsAppCloud ? "Token de Acceso Permanente (System User Token)" : "Page Access Token"}
          name="facebookPageToken"
          value={form.facebookPageToken}
          onChange={onChange}
          placeholder={isEdit ? "Token de acceso guardado" : "EAABwzLixnjY..."}
          type="password"
          canCopy={true}
          canToggleVisibility={true}
          required={!isEdit}
          helpText={
            isWhatsAppCloud
              ? "Token de sistema permanente en: Configuración del negocio → Usuarios del sistema"
              : "Token permanente en: Meta Developers → Tu App → Herramientas para APIs de Graph"
          }
        />
        {isEdit && form.facebookVerifyToken && (
          <Field
            label="Token de Verificación del Webhook (Verify Token)"
            name="facebookVerifyToken"
            value={form.facebookVerifyToken}
            onChange={onChange}
            canCopy={true}
            helpText="Token usado por Meta para verificar las notificaciones del Webhook"
          />
        )}
      </div>
    </div>
  );
};

const StepWebhook = ({ channel, form, webhookUrl, onVerify, verifyState }) => {
  const isWhatsAppCloud = channel === "whatsapp_cloud" || channel === "whatsapp_coexistence";
  const callbackFields = isWhatsAppCloud
    ? ["messages"]
    : channel === "facebook"
    ? ["messages", "messaging_postbacks", "messaging_optins"]
    : ["messages", "messaging_seen"];

  return (
    <div className="space-y-5">
      <p className="text-sm text-slate-600 dark:text-slate-400">
        Configura el webhook en Meta Developer Console para que {isWhatsAppCloud ? "WhatsApp" : channel === "facebook" ? "Facebook" : "Instagram"} envíe los mensajes a tu plataforma.
      </p>

      <div className="space-y-3">
        <div>
          <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">
            1. URL de Callback (Webhook)
          </p>
          <CodeBlock value={webhookUrl || "Cargando..."} />
          <p className="mt-1 text-xs text-slate-400">
            Pega esta URL en: Meta Developers → Tu App → {isWhatsAppCloud ? "WhatsApp → Configuración" : "Webhooks"} → Callback URL
          </p>
        </div>

        <div>
          <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">
            2. Token de Verificación
          </p>
          <CodeBlock value={form.facebookVerifyToken} />
          <p className="mt-1 text-xs text-slate-400">Pega este token en el campo "Verify Token" de Meta</p>
        </div>

        <div>
          <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">
            3. Campos de suscripción
          </p>
          <div className="bg-slate-100 dark:bg-slate-800 rounded-lg px-3 py-2 border border-slate-200 dark:border-slate-700">
            {callbackFields.map(f => (
              <code key={f} className="block text-xs text-slate-700 dark:text-slate-300 font-mono">{f}</code>
            ))}
          </div>
          <p className="mt-1 text-xs text-slate-400">Activa estas suscripciones en el webhook de tu app</p>
        </div>

        <div>
          <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">
            4. Verificar conexión
          </p>
          <button
            type="button"
            onClick={onVerify}
            disabled={verifyState === "loading"}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-60 transition-colors"
          >
            {verifyState === "loading"
              ? <><Loader2 className="w-4 h-4 animate-spin" /> Verificando...</>
              : verifyState === "ok"
              ? <><CheckCircle className="w-4 h-4 text-emerald-300" /> Verificado</>
              : verifyState === "error"
              ? <><AlertCircle className="w-4 h-4 text-red-300" /> Reintentar</>
              : <><RefreshCw className="w-4 h-4" /> Verificar Token de Acceso</>
            }
          </button>
          {verifyState === "ok" && (
            <p className="mt-1.5 text-xs text-emerald-600 dark:text-emerald-400 font-medium">
              ✓ Token válido — la conexión está lista
            </p>
          )}
          {verifyState === "error" && (
            <p className="mt-1.5 text-xs text-red-500 font-medium">
              Token inválido. Verifica que el ID de teléfono y Token sean correctos.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const StepQueues = ({ selectedQueueIds, onChange }) => (
  <div className="space-y-4">
    <p className="text-sm text-slate-600 dark:text-slate-400">
      Asigna colas de atención para los mensajes entrantes de este canal (opcional).
    </p>
    <QueueSelect selectedQueueIds={selectedQueueIds} onChange={onChange} />
  </div>
);

// ─── Main Modal ───────────────────────────────────────────────────────────────

const MetaConnectionModal = ({ open, onClose, connectionId, initialChannel = "facebook" }) => {
  const isEdit = !!connectionId;

  const initialForm = {
    name: "",
    facebookAppId: "",
    facebookAppSecret: "",
    facebookPageId: "",
    facebookPageToken: "",
    facebookVerifyToken: generateVerifyToken(),
    wabaId: ""
  };

  const [step, setStep] = useState(0);
  const [channel, setChannel] = useState(initialChannel);
  const [form, setForm] = useState(initialForm);
  const [selectedQueueIds, setSelectedQueueIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [verifyState, setVerifyState] = useState("idle");

  // Meta OAuth specific states
  const [globalAppId, setGlobalAppId] = useState("");
  // Versión del diálogo de OAuth. La manda el backend (única fuente de verdad);
  // el fallback existe solo para no dejar la ventana sin abrir si falla.
  const [graphVersion, setGraphVersion] = useState("v23.0");
  const [useManual, setUseManual] = useState(false);
  const [loadingPages, setLoadingPages] = useState(false);
  const [pagesList, setPagesList] = useState([]);
  const [selectedPage, setSelectedPage] = useState(null);

  // Sync channel with initialChannel on open
  useEffect(() => {
    if (open) {
      setChannel(initialChannel);
    }
  }, [open, initialChannel]);

  // Load global oauth App ID on open
  useEffect(() => {
    if (!open) return;
    api.get("/api/meta/oauth/url")
      .then(({ data }) => {
        if (data.graphVersion) setGraphVersion(data.graphVersion);
        if (data.appId) {
          setGlobalAppId(data.appId);
          setUseManual(false);
        } else {
          setGlobalAppId("");
          setUseManual(true);
        }
      })
      .catch(() => {
        setGlobalAppId("");
        setUseManual(true);
      });
  }, [open]);

  // Load webhook base URL once
  useEffect(() => {
    if (!open) return;
    api.get("/api/meta/webhook-url")
      .then(({ data }) => setWebhookUrl(data.webhookUrl))
      .catch(() => setWebhookUrl(window.location.origin.replace(/:\d+$/, ":8080") + "/api/meta/webhook"));
  }, [open]);

  // Load existing connection data on edit
  useEffect(() => {
    if (!open || !connectionId) return;
    api.get(`/whatsapp/${connectionId}`).then(({ data }) => {
      setChannel(data.channel || "facebook");
      setForm({
        name: data.name || "",
        facebookAppId: data.facebookAppId || "",
        facebookAppSecret: data.facebookAppSecret || "",
        facebookPageId: data.facebookPageId || "",
        facebookPageToken: data.facebookPageToken || "",
        facebookVerifyToken: data.facebookVerifyToken || generateVerifyToken(),
        wabaId: data.wabaId || ""
      });
      setSelectedQueueIds(data.queues?.map(q => q.id) || []);
      setUseManual(true); // Force manual mode on edit to see fields
      setStep(1);
    }).catch(toastError);
  }, [open, connectionId]);

  const handleClose = useCallback(() => {
    setStep(0);
    setChannel(initialChannel);
    setForm(initialForm);
    setSelectedQueueIds([]);
    setVerifyState("idle");
    setPagesList([]);
    setSelectedPage(null);
    setLoadingPages(false);
    onClose();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose, initialChannel]);

  const handleFieldChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleCopyAllCredentials = () => {
    const data = {
      channel,
      name: form.name,
      facebookAppId: form.facebookAppId,
      facebookAppSecret: form.facebookAppSecret,
      facebookPageId: form.facebookPageId,
      wabaId: form.wabaId,
      facebookPageToken: form.facebookPageToken,
      facebookVerifyToken: form.facebookVerifyToken,
      webhookUrl: webhookUrl || ""
    };
    navigator.clipboard.writeText(JSON.stringify(data, null, 2)).then(() => {
      toast.success("Credenciales completas copiadas al portapapeles en formato JSON");
    }).catch(() => {
      toast.error("No se pudo copiar automáticamente");
    });
  };

  const handleFacebookLogin = () => {
    if (!globalAppId) return;

    const width = 650;
    const height = 650;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const redirectUri = window.location.origin;
    const scopes = (channel === "whatsapp_cloud" || channel === "whatsapp_coexistence")
      ? [
          "whatsapp_business_management",
          "whatsapp_business_messaging"
        ].join(",")
      : [
          "pages_show_list",
          "pages_read_engagement",
          "pages_manage_metadata",
          "pages_messaging",
          // Dependencia obligatoria de los permisos de Páginas segun Meta:
          // sin ella el token sale sin los pages_* aunque el usuario acepte,
          // y hay que declararla en la revisión de la app.
          "business_management",
          "instagram_basic",
          "instagram_manage_messages"
        ].join(",");

    const oauthUrl = `https://www.facebook.com/${graphVersion}/dialog/oauth?client_id=${globalAppId}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=${scopes}&response_type=token`;

    const popup = window.open(oauthUrl, "facebook-login", `width=${width},height=${height},left=${left},top=${top}`);

    const checkPopupInterval = setInterval(async () => {
      if (!popup || popup.closed) {
        clearInterval(checkPopupInterval);
        return;
      }
      try {
        const currentUrl = popup.location.href;
        if (currentUrl.includes("access_token=")) {
          clearInterval(checkPopupInterval);
          const hash = popup.location.hash || popup.location.search;
          const params = new URLSearchParams(hash.replace("#", "?"));
          const userToken = params.get("access_token");
          popup.close();
          if (userToken) {
            fetchUserPages(userToken);
          }
        }
      } catch (err) {
        // Cross-origin errors expected while on facebook.com
      }
    }, 500);
  };

  const fetchUserPages = async (userAccessToken) => {
    setLoadingPages(true);
    try {
      const { data } = await api.post("/api/meta/oauth/pages", { userAccessToken, channel });
      setPagesList(data.pages || []);
      if ((data.pages || []).length === 0) {
        toast.info((channel === "whatsapp_cloud" || channel === "whatsapp_coexistence") 
          ? "No se encontraron cuentas de WhatsApp Business vinculadas."
          : "No se encontraron páginas de Facebook vinculadas a esta cuenta."
        );
      }
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingPages(false);
    }
  };

  const handleVerify = async () => {
    // On edit, a blank token means "keep the stored one" — the backend
    // ignores empty token/secret on update and verifies with the saved value.
    if (!form.facebookPageId || (!connectionId && !form.facebookPageToken)) {
      toast.error("Completa las credenciales antes de verificar");
      return;
    }
    setVerifyState("loading");
    try {
      const payload = { ...form, channel, queueIds: selectedQueueIds };
      let savedId = connectionId;
      if (!savedId) {
        const { data } = await api.post("/whatsapp", payload);
        savedId = data.id;
      } else {
        await api.put(`/whatsapp/${savedId}`, payload);
      }
      const { data } = await api.post(`/api/meta/verify-connection/${savedId}`);
      if (data.ok) {
        setVerifyState("ok");
        toast.success(`Conectado exitosamente: ${data.pageName || "WhatsApp / Página"}`);
        if (data.subscribed) {
          toast.success("Webhook suscrito: ya puedes recibir mensajes.");
        } else {
          toast.warn(
            `Conexión válida, pero no se pudo suscribir el webhook automáticamente${data.subscribeError ? `: ${data.subscribeError}` : ""}. Suscribe la página manualmente en Meta.`
          );
        }
        // Los campos de webhook se marcan a nivel de app (App Dashboard →
        // WhatsApp → Configuration) y la API no puede activarlos. En
        // coexistencia son cuatro: sin `smb_message_echoes` la plataforma no
        // ve lo que el negocio responde desde el celular, y sin `history` no
        // se importa nada de lo anterior.
        if (data.requiredWebhookFields?.length > 1) {
          toast.info(
            `Verifica en Meta (App Dashboard → WhatsApp → Configuration) que estén marcados estos campos de webhook: ${data.requiredWebhookFields.join(", ")}.`,
            { autoClose: 12000 }
          );
        }
      } else {
        setVerifyState("error");
        toast.error(data.error || "Error al verificar");
      }
    } catch (err) {
      setVerifyState("error");
      toastError(err);
    }
  };

  const handleSave = async () => {
    // OAuth auto connect flow
    if (globalAppId && !useManual) {
      if (!selectedPage) {
        toast.error("Por favor, selecciona una cuenta para conectar");
        return;
      }
      setSaving(true);
      try {
        const payload = {
          channel,
          name: selectedPage.name,
          id: selectedPage.id,
          facebookPageId: selectedPage.facebookPageId,
          pageToken: selectedPage.pageToken,
          wabaId: selectedPage.wabaId,
          queueIds: selectedQueueIds
        };
        await api.post("/api/meta/oauth/connect", payload);
        toast.success("Conexión realizada con éxito");
        handleClose();
      } catch (err) {
        toastError(err);
      } finally {
        setSaving(false);
      }
      return;
    }

    // Classic manual flow
    if (!form.name.trim()) { toast.error("El nombre es requerido"); return; }
    if (!form.facebookPageId.trim()) { toast.error("El ID es requerido"); return; }
    if ((channel === "whatsapp_cloud" || channel === "whatsapp_coexistence") && !form.wabaId.trim()) { toast.error("El WABA ID es requerido"); return; }
    if (!connectionId && !form.facebookPageToken.trim()) { toast.error("El Token de Acceso es requerido"); return; }

    setSaving(true);
    try {
      const payload = { ...form, channel, queueIds: selectedQueueIds };
      if (connectionId) {
        await api.put(`/whatsapp/${connectionId}`, payload);
      } else {
        await api.post("/whatsapp", payload);
      }
      toast.success(isEdit ? "Conexión actualizada" : "Conexión creada correctamente");
      handleClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const getSteps = () => {
    if (globalAppId && !useManual) {
      return [
        { id: "channel", title: "Canal" },
        { id: "oauth", title: "Conexión" },
        { id: "queues", title: "Colas" }
      ];
    }
    return [
      { id: "channel", title: "Canal" },
      { id: "credentials", title: "Credenciales" },
      { id: "webhook", title: "Webhook" },
      { id: "queues", title: "Colas" }
    ];
  };

  const canGoNext = () => {
    if (step === 0) return !!channel;
    if (globalAppId && !useManual) {
      if (step === 1) return !!selectedPage;
      return true;
    }
    if (step === 1) {
      const basic = form.name.trim() && form.facebookAppId.trim() && form.facebookPageId.trim() && (isEdit || form.facebookPageToken.trim());
      if (channel === "whatsapp_cloud" || channel === "whatsapp_coexistence") {
        return basic && form.wabaId.trim();
      }
      return basic;
    }
    return true;
  };

  if (!open) return null;

  const stepsList = getSteps();
  const channelLabel = channel === "whatsapp_coexistence" ? "WhatsApp Coexistencia" : channel === "whatsapp_cloud" ? "WhatsApp Cloud" : channel === "facebook" ? "Facebook Messenger" : "Instagram DM";
  const isInstagramSelection = channel === "instagram";
  const isWhatsAppCloudSelection = channel === "whatsapp_cloud" || channel === "whatsapp_coexistence";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />
      <div className={`relative bg-white dark:bg-slate-900 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 w-full mx-4 max-h-[92vh] flex flex-col transition-all duration-300 ${
        step === 0 ? "max-w-3xl" : "max-w-lg"
      }`}>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700 flex-none">
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <ChannelIcon channel={channel} size={18} />
            {isEdit ? `Editar ${channelLabel}` : `Nueva conexión`}
          </h2>
          <button onClick={handleClose} className="p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-0 px-5 py-3 border-b border-slate-200 dark:border-slate-800 flex-none bg-slate-50 dark:bg-slate-800/30">
          {stepsList.map((s, i) => (
            <React.Fragment key={s.id}>
              <div className={`flex items-center gap-1.5 ${i <= step ? "text-indigo-600 dark:text-indigo-400" : "text-slate-400"}`}>
                <span className={`w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center ${
                  i < step ? "bg-indigo-600 text-white" :
                  i === step ? "border-2 border-indigo-600 text-indigo-600" :
                  "border-2 border-slate-300 dark:border-slate-600 text-slate-400"
                }`}>
                  {i < step ? <Check className="w-3 h-3" /> : i + 1}
                </span>
                <span className="text-[11px] font-semibold hidden sm:block">{s.title}</span>
              </div>
              {i < stepsList.length - 1 && (
                <div className={`h-px flex-1 mx-2 ${i < step ? "bg-indigo-400" : "bg-slate-200 dark:bg-slate-700"}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 bg-slate-50 dark:bg-slate-800/20">
          {step === 0 && <StepChannelSelect channel={channel} onChange={setChannel} />}
          
          {/* OAUTH FLOW - STEP 1 */}
          {globalAppId && !useManual && step === 1 && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Inicia sesión con tu cuenta de Facebook para listar y conectar automáticamente tus {isWhatsAppCloudSelection ? "números de WhatsApp Cloud" : isInstagramSelection ? "cuentas de Instagram" : "páginas de Facebook"}.
              </p>

              {pagesList.length === 0 && !loadingPages && (
                <button
                  type="button"
                  onClick={handleFacebookLogin}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold bg-[#1877F2] hover:bg-[#166FE5] text-white rounded-xl shadow-sm transition-colors"
                >
                  <Facebook className="w-5 h-5 fill-current" />
                  Conectar con Facebook
                </button>
              )}

              {loadingPages && (
                <div className="flex flex-col items-center justify-center py-8 gap-2">
                  <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
                  <p className="text-xs text-slate-500">Obteniendo tus canales desde Meta...</p>
                </div>
              )}

              {pagesList.length > 0 && !loadingPages && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      Selecciona {isWhatsAppCloudSelection ? "el número de WhatsApp" : isInstagramSelection ? "la cuenta de Instagram" : "la página"} a conectar:
                    </p>
                    <button
                      type="button"
                      onClick={handleFacebookLogin}
                      className="text-xs text-indigo-600 hover:text-indigo-700 font-medium underline flex items-center gap-1"
                    >
                      <RefreshCw className="w-3 h-3" /> Cambiar de cuenta
                    </button>
                  </div>

                  <div className="space-y-2 max-h-[260px] overflow-y-auto pr-1">
                    {pagesList.map((p) => {
                      if (isInstagramSelection && !p.instagram) return null;

                      const key = isInstagramSelection ? p.instagram.id : p.pageId;
                      const name = isInstagramSelection ? p.instagram.name : p.pageName;
                      const avatar = isInstagramSelection ? p.instagram.profilePictureUrl : p.pageAvatar;
                      const subtext = isWhatsAppCloudSelection ? `WABA ID: ${p.wabaId}` : isInstagramSelection ? `@${p.instagram.username}` : `Facebook Page ID: ${p.pageId}`;
                      
                      const isSelected = selectedPage?.id === key;

                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() => setSelectedPage({
                            id: key,
                            name,
                            facebookPageId: p.pageId,
                            pageToken: p.pageToken,
                            wabaId: p.wabaId
                          })}
                          className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-all ${
                            isSelected
                              ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-950/30"
                              : "border-slate-200 dark:border-slate-700 hover:border-indigo-300 dark:hover:border-indigo-800 bg-white dark:bg-slate-900"
                          }`}
                        >
                          {avatar ? (
                            <img src={avatar} alt={name} className="w-10 h-10 rounded-full object-cover bg-slate-100" />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-950 flex items-center justify-center font-bold text-indigo-600">
                              {name[0]}
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">{name}</p>
                            <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{subtext}</p>
                          </div>
                          {isSelected && (
                            <CheckCircle className="w-5 h-5 text-indigo-600 flex-shrink-0" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="pt-3 border-t border-slate-200 dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => setUseManual(true)}
                  className="w-full text-center text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition-colors underline"
                >
                  ¿Prefieres configurar credenciales de forma manual? Usar configuración manual
                </button>
              </div>
            </div>
          )}

          {/* MANUAL FLOW - STEP 1 */}
          {(!globalAppId || useManual) && step === 1 && (
            <StepCredentials
              channel={channel}
              form={form}
              onChange={handleFieldChange}
              isEdit={isEdit}
              webhookUrl={webhookUrl}
              onCopyAll={handleCopyAllCredentials}
            />
          )}

          {/* MANUAL FLOW - STEP 2 */}
          {(!globalAppId || useManual) && step === 2 && (
            <StepWebhook
              channel={channel}
              form={form}
              webhookUrl={webhookUrl}
              onVerify={handleVerify}
              verifyState={verifyState}
            />
          )}

          {/* QUEUES STEP */}
          {((globalAppId && !useManual && step === 2) || ((!globalAppId || useManual) && step === 3)) && (
            <StepQueues selectedQueueIds={selectedQueueIds} onChange={setSelectedQueueIds} />
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 px-5 py-4 border-t border-slate-200 dark:border-slate-700 flex-none">
          <button
            type="button"
            onClick={() => step > 0 ? setStep(s => s - 1) : handleClose()}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            {step === 0 ? "Cancelar" : "Atrás"}
          </button>

          {step < stepsList.length - 1 ? (
            <button
              type="button"
              onClick={() => setStep(s => s + 1)}
              disabled={!canGoNext()}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
            >
              Siguiente <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
            >
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {isEdit ? "Guardar cambios" : "Guardar conexión"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default MetaConnectionModal;
